(() => {
  if (customElements.get('cc-grand-prix')) return;

  class CCGrandPrix extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || this.controller) return;
        this.controller = new AbortController();
        const options = { signal: this.controller.signal };
        this.input = this.querySelector('[data-event-name-input]');
        this.name = this.querySelector('[data-event-name-text]');
        this.shirt = this.querySelector('[data-event-shirt]');
        if (this.input && this.name && this.shirt) {
          this.input.addEventListener('input', () => this.updateName(), options);
          this.resizeObserver = new ResizeObserver(() => this.fitName());
          this.resizeObserver.observe(this.shirt);
          this.updateName();
          document.fonts.ready.then(() => { if (this.isConnected) this.fitName(); });
        }
        this.updateCountdown();
        this.timer = setInterval(() => this.updateCountdown(), 30000);
        this.setupAudio(options);
        this.setupFilm(options);
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      clearInterval(this.timer);
      this.resizeObserver?.disconnect();
      this.querySelectorAll('audio, video').forEach(media => media.pause());
    }

    updateName() {
      const value = this.input.value.trim().replace(/[\u0000-\u001f]/g, '').toLocaleUpperCase() || 'YOUR NAME';
      this.name.textContent = value;
      this.fitName();
      const announcement = this.querySelector('[data-event-name-announcement]');
      if (announcement) announcement.textContent = `Concept jersey with ${value} across the back.`;
    }

    fitName() {
      if (!this.name) return;
      this.name.style.transform = 'none';
      let size = 25;
      this.name.style.fontSize = `${size}px`;
      const available = this.name.parentElement.clientWidth;
      while (size > 11 && this.name.scrollWidth > available) {
        this.name.style.fontSize = `${--size}px`;
      }
      if (available > 0 && this.name.scrollWidth > available) {
        this.name.style.transform = `scaleX(${available / this.name.scrollWidth})`;
      }
    }

    updateCountdown() {
      const countdown = this.querySelector('[data-event-countdown]');
      const label = this.querySelector('[data-event-countdown-label]');
      if (!countdown || !label) return;
      const opening = Date.parse(this.dataset.opening);
      if (!Number.isFinite(opening)) {
        label.textContent = 'Race weekend';
        countdown.textContent = this.dataset.eventDates;
        return;
      }
      const remaining = opening - Date.now();
      if (remaining <= 0) {
        label.textContent = 'Race weekend';
        countdown.textContent = this.dataset.eventDates;
        return;
      }
      const days = Math.floor(remaining / 86400000);
      const hours = Math.floor(remaining / 3600000) % 24;
      const minutes = Math.floor(remaining / 60000) % 60;
      label.textContent = 'Opening Night in';
      countdown.textContent = `${days}d  ${String(hours).padStart(2, '0')}h  ${String(minutes).padStart(2, '0')}m`;
      countdown.setAttribute('aria-label', `Opening Night in ${days} days, ${hours} hours, ${minutes} minutes`);
    }

    setupAudio(options) {
      const audio = this.querySelector('[data-event-audio]');
      const button = this.querySelector('[data-event-audio-toggle]');
      const label = this.querySelector('[data-event-audio-label]');
      const status = this.querySelector('[data-event-audio-status]');
      if (!audio || !button || !label) return;
      audio.hidden = true;
      button.hidden = false;
      const update = () => {
        const playing = !audio.paused;
        button.setAttribute('aria-pressed', String(playing));
        button.setAttribute('aria-label', `${playing ? 'Pause' : 'Play'} ANAYA's audio clip`);
        label.textContent = playing ? 'Pause ANAYA' : 'Hear ANAYA';
      };
      for (const event of ['play', 'pause', 'ended']) audio.addEventListener(event, update, options);
      button.addEventListener('click', async () => {
        if (!audio.paused) { audio.pause(); return; }
        try { if (status) status.textContent = ''; await audio.play(); }
        catch { if (status) status.textContent = 'The clip could not load. Please try again.'; }
      }, options);
    }

    setupFilm(options) {
      const film = this.querySelector('[data-event-film]');
      const video = film?.querySelector('video');
      const button = this.querySelector('[data-event-film-toggle]');
      if (!video || !button) return;
      const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
      video.muted = true;
      button.hidden = false;
      const update = () => {
        const playing = !video.paused;
        film.classList.toggle('event-film-active', playing || video.currentTime > 0);
        button.textContent = playing ? 'Pause film' : 'Play film';
        button.setAttribute('aria-label', playing ? 'Pause hero film' : 'Play hero film');
      };
      video.addEventListener('play', update, options);
      video.addEventListener('pause', update, options);
      preference.addEventListener('change', () => { if (preference.matches) video.pause(); }, options);
      button.addEventListener('click', () => {
        if (video.paused) video.play().catch(() => { button.textContent = 'Play film'; });
        else video.pause();
      }, options);
      if (!preference.matches) video.play().catch(() => {});
    }
  }
  customElements.define('cc-grand-prix', CCGrandPrix);
})();
